import { Breadcrumb, Button, Layout, Space, Spin, Table, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { IDictionary } from '~utilities/IDictionary';
import AddModal from './AddModal';
import './loan-payment-abas.less';
import { currencyFormatter } from '~utilities/formatters';
import ModalStateEnum from '~UI/ModalStateEnum';
import ILoanPaymentAba from '~Api/LoanPaymentAba/ILoanPaymentAba';
import { Dispatch } from 'redux';
import { Helmet } from 'react-helmet';
import { loanPaymentAbasSelector } from './selectors';
import { loanPaymentAbasListAction } from './actions';

export default function List(): ReactElement {
    const [ modalState, setModalState ] = useState<ModalStateEnum>(ModalStateEnum.Off);

    const loanPaymentAbas: IDictionary<ILoanPaymentAba> = useSelector(loanPaymentAbasSelector);

    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        if (!loanPaymentAbas) {
            dispatch(loanPaymentAbasListAction());
        }
    }, [
        dispatch,
        loanPaymentAbas,
    ]);

    const onClickAddLoanPaymentAba: () => void = useCallback(() => {
        setModalState(ModalStateEnum.Open);
    }, []);

    const onCloseAddLoanPaymentModal: () => void = useCallback(() => {
        setModalState(ModalStateEnum.Closed);
    }, []);

    if (!loanPaymentAbas) {
        return (
            <Layout>
                <Helmet>
                    <title>Loan Payment ABAs</title>
                </Helmet>
                <Breadcrumb className='breadcrumb'>
                    <Breadcrumb.Item>Home</Breadcrumb.Item>
                    <Breadcrumb.Item><Link to='/loans'>Loans</Link></Breadcrumb.Item>
                    <Breadcrumb.Item>Loan Payment ABAs</Breadcrumb.Item>
                </Breadcrumb>
                <Layout className='content-wrapper'>
                    <Layout.Content className='content'>
                        <Typography.Title level={2}>Loan Payment ABAs</Typography.Title>
                        <Spin />
                    </Layout.Content>
                </Layout>
            </Layout>
        );
    }

    const columns: ColumnsType<ILoanPaymentAba> = [
        {
            dataIndex: 'createdTime',
            defaultSortOrder: 'descend',
            render: (createdTime: string, loanPaymentAba: ILoanPaymentAba) => <Link to={`/loans/payment-abas/${loanPaymentAba.uuid}/transactions`}>{dayjs(createdTime).format('DD/MM/YYYY HH:mm')}</Link>,
            sorter: (a: ILoanPaymentAba, b: ILoanPaymentAba) => dayjs(a.createdTime) > dayjs(b.createdTime) ? 1 : -1,
            title: 'Created',
        },
        {
            dataIndex: 'transactionCount',
            title: 'Transaction Count',
            width: 200,
        },
        {
            dataIndex: 'transactionAmountTotal',
            render: (transactionAmountTotal: number) => currencyFormatter.format(transactionAmountTotal),
            title: 'Interest Total',
            width: 200,
        },
    ];

    const loanPaymentAddModal: JSX.Element = ModalStateEnum.Off !== modalState && (
        <AddModal
            isOpen={ModalStateEnum.Open === modalState}
            onCancel={onCloseAddLoanPaymentModal}
        />
    );

    return (
        <Layout>
            <Helmet>
                <title>Loan Payment ABAs</title>
            </Helmet>
            <Breadcrumb className='breadcrumb'>
                <Breadcrumb.Item>Home</Breadcrumb.Item>
                <Breadcrumb.Item><Link to='/loans'>Loans</Link></Breadcrumb.Item>
                <Breadcrumb.Item>Loan Payment ABAs</Breadcrumb.Item>
            </Breadcrumb>
            <Layout className='content-wrapper'>
                <Layout.Content className='content'>
                    <Space className='actions'>
                        <Button onClick={onClickAddLoanPaymentAba}>Add Loan Payment ABA</Button>
                    </Space>
                    <Typography.Title level={2}>Loan Payment ABAs</Typography.Title>
                    <Table
                        columns={columns}
                        dataSource={_.values(loanPaymentAbas)}
                        pagination={{ defaultPageSize: 50 }}
                        rowKey='uuid'
                        size='middle'
                    />
                </Layout.Content>
            </Layout>
            {loanPaymentAddModal}
        </Layout>
    );
}
